import { useTranslation } from "react-i18next";
import defaultContent from "../../../content/ssiUplineReport";
import { SsiTrackingPeriod, SSIUplineResponse } from "../../../services/SSIUpline/ssiUplineAPI.types";
import { getFullMonth } from "../../../common/util/period";
import { storedLocale } from "../../Authentication/util";
import { SsiOverviewResponse } from "../../../services/StrongStart/ssiAPI.types";
import { SSIPreWindowRow } from "./PreWindowRow";
import { IconAlertWhite } from "../../../common/icons/IconAlertWhite/IconAlertWhite";
import React, { useEffect, useRef, useState } from "react";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import "./PreWindow.scss";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import breakpoints from "../../../common/scss/breakpoints.module.scss";
import useWindowSize from "../../../common/hooks/useWindowSize";
import { track } from "../../../common/util/tealium";

interface Props {
  ssiUplineData: SSIUplineResponse;
  isLoading: boolean;
  selectedAboNumber: string;
}

export default function PreWindow({ ssiUplineData, isLoading, selectedAboNumber }: Props) {
  const { t } = useTranslation(["ssiUplineReport"]);
  const periods = [...ssiUplineData.PreWindow.Periods].sort((a, b) => Number(a.Period) - Number(b.Period));
  const scrollRef = useRef<HTMLDivElement>(null);
  const noData = !isLoading && periods.length > 0 && periods.every((period) => period.Abos.length === 0);
  const [isDesktopView, setIsDesktopView] = useState(false);
  const { width } = useWindowSize();
  const confidentialUser = t("common:confidentialUser", defaultContent["confidentialUser"]).toString();

  useEffect(() => {
    if (width) {
      setIsDesktopView(Number(width) >= Number(breakpoints["tabletSmall"]));
    }
  }, [width]);

  const renderTag = () => {
    return (
      <div className="SSIPreWindow__body--section--tag">
        <IconAlertWhite /> <span>{t("lastChance", defaultContent.lastChance as string)}</span>
      </div>
    );
  };

  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    }
  }, [selectedAboNumber]);

  const SkeletonLoaderRow = () => (
    <SSIPreWindowRow
      aff={""}
      aboName={""}
      aboLocalName={""}
      aboNum={""}
      sponsorName={""}
      sponsorNum={""}
      eligibilityWindow={{
        EntryPeriod: "",
        StartPeriod: "",
        EndPeriod: "",
        CurrentYear: [],
        NextYear: [],
        PayDayMonths: [],
        MarketHasGracePeriod: false,
      }}
      requirements={{
        VolumeRequirements: [],
        PercentRequirements: [],
        TotalRequirements: 0,
        RequirementsMet: 0,
        RequirementMetStatuses: [],
      }}
      isLoading={true}
      isHighlighted={false}
      isDesktopView={isDesktopView}
    />
  );

  if (isLoading) {
    return (
      <div className="SSIPreWindow">
        <div className="SSIPreWindow__body">
          <div className="SSIPreWindow__body--section">
            <div className="SSIPreWindow__body--section--title">
              <LeafRectangleLoader isLoading={true} width="40%" height="1.5rem" margin="0rem" />
            </div>
            <div className="SSIPreWindow__body--section--content">
              <SkeletonLoaderRow />
              <SkeletonLoaderRow />
            </div>
          </div>

          <div className="SSIPreWindow__body--section">
            <div className="SSIPreWindow__body--section--title">
              <LeafRectangleLoader isLoading={true} width="40%" height="1.5rem" margin="0rem" />
            </div>
            <div className="SSIPreWindow__body--section--content">
              <SkeletonLoaderRow />
              <SkeletonLoaderRow />
            </div>
          </div>

          <div className="SSIPreWindow__body--section">
            <div className="SSIPreWindow__body--section--title">
              <LeafRectangleLoader isLoading={true} width="40%" height="1.5rem" margin="0rem" />
            </div>
            <div className="SSIPreWindow__body--section--content">
              <SkeletonLoaderRow />
              <SkeletonLoaderRow />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="SSIPreWindow">
      {!isLoading && noData ? (
        <ErrorPage
          errorType={ERROR_TYPE.NO_DOWNLINES}
          iconType={IconType.SearchCriteria}
          tealiumTrackFunction={() => track("view", "", "pre-window_tab", "ssi_upline_report", "no_downlines", "")}
        />
      ) : (
        <>
          <div className="SSIPreWindow__body">
            {periods.map((period: SsiTrackingPeriod, ind) => {
              const month = getFullMonth(period.Period, storedLocale());
              return (
                <React.Fragment key={ind + period.Period}>
                  {period.Abos.length > 0 && (
                    <div className="SSIPreWindow__body--section">
                      <div className="SSIPreWindow__body--section--title">
                        {t("monthEntry", defaultContent.monthEntry as string, {
                          month: month,
                          abos: period.Abos.length,
                        })}
                        {ind === 0 && renderTag()}
                      </div>
                      <div className="SSIPreWindow__body--section--content">
                        {period.Abos.map((abo: SsiOverviewResponse) => {
                          return (
                            <div
                              className="SSIPreWindow__body--section--content--text"
                              key={abo.Abo}
                              ref={abo.Abo === selectedAboNumber ? scrollRef : undefined}
                            >
                              <SSIPreWindowRow
                                key={abo.Abo}
                                aff={abo.Aff}
                                aboName={abo.AboName && abo.AboName.length > 0 ? abo.AboName : confidentialUser}
                                aboLocalName={abo.AboName}
                                aboNum={abo.Abo}
                                sponsorName={
                                  abo.SponsorName && abo.SponsorName.length > 0 ? abo.SponsorName : confidentialUser
                                }
                                sponsorNum={abo.SponsorABO}
                                eligibilityWindow={abo.EligibilityWindow}
                                requirements={abo.Requirements}
                                isLoading={isLoading}
                                isHighlighted={abo.Abo === selectedAboNumber}
                                isDesktopView={isDesktopView}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
